import { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate, useLocation, Link } from "react-router-dom"
import { toast } from 'react-toastify'
import classnames from 'classnames'
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Server from "../../Shared/Server"
import { FaFileInvoice } from "react-icons/fa";
import Spinner from '../../Shared/Spinner'
import BackButton from '../../Shared/BackButton'
import {months_list} from "../../Shared/List";
import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { FaTimes, FaFileExport } from "react-icons/fa"

const BilledDetail = () => {
    const [billed, setBilled] = useState([])
    const [pinnedBottomRowData, setPinnedBottomRowData] = useState([])
    const [title, setTitle] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [show, setShow] = useState(false);
    const [publicID, setPublicID] = useState()
    const gridRef = useRef();
    const navigate = useNavigate();
    const location = useLocation();
    const [formData, setFormData] = useState({year: 0, month: 0})

    useEffect(() => {
        if (location.state){
            const {data} = location.state
            setFormData((prev) => ({
                ...prev,
                year: data.year,
                month: data.month
            }));

            fetchData({
                year: data.year,
                month: data.month,
                by: data?.by,
                public_id: data.public_id // public_id for collector/zone/village and etc
            })
            setTitle(`${data.name}, ${months_list[data.month - 1].name} - ${data.year} Billed Customers`)

        }

    }, [])

    const columnDefs = useMemo(() => [
        {
            headerName: "#",
            filter: false,
            maxWidth: 70,
            sortable: true,
            valueFormatter: function (params) {
                if (params.node.rowPinned) return '';
                if (isNaN(params.node.id)) {
                    return "";
                }
                return `${Number(params.node.rowIndex) + 1}`;
            },
        },
        {
            headerName: "Actions",
            pinned: 'left',
            minWidth: 120,
            sortable: false,
            filter: false,
            cellRenderer: (e) => {
                if (!e.data) return null;
                if (e.node.rowPinned) return '';
                const isPaid = e.data.is_paid === true;
                const payload = {
                    year: formData.year,
                    month: formData.month,
                    contact_number: e.data.contact_number,
                };
                return (
                    <div className="d-flex gap-1">
                        <button
                            className={classnames('btn btn-outline-danger', {'disabled': isPaid})}
                            onClick={() => { if (!isPaid) handleShow(e.data.public_id); }}
                            title={isPaid ? 'Cannot cancel a paid bill' : 'Cancel this bill'}
                        >
                            <FaTimes/>
                        </button>
                        <Link
                            className="btn btn-outline-success"
                            to="/invoice"
                            state={{ payload: payload }}
                            title="View invoice"
                        >
                            <FaFileInvoice/>
                        </Link>
                    </div>
                );
            },
        },
        {
            headerName: "Date",
            field: "date",
            sortable: true,
            filter: true,
        },
        {
            headerName: "PID",
            field: "previous_id",
            sortable: true,
            filter: true,
        },
        {
            headerName: "ID",
            field: "location_id",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Name",
            field: "full_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Remark",
            field: "remark",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Contact",
            field: "contact_number",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Prev.Reading",
            field: "previous_reading",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Curr.Reading",
            field: "current_reading",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Difference",
            field: "difference",
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB
        },
        {
            headerName: "Prev.Balance",
            field: "previous_balance",
            sortable: true,
            filter: 'agNumberColumnFilter'
        },
        {
            headerName: "Debit",
            field: "debit",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Total",
            field: "total",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Rate",
            field: "rate_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Village",
            field: "village_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Zone",
            field: "zone_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Station",
            field: "station_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Line",
            field: "line_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "MeterID",
            field: "serial_number",
            sortable: true,
            filter: true,
        },
        {
            headerName: "LockID",
            field: "lock_number",
            sortable: true,
            filter: true,
        },
    ], [formData.year, formData.month])

    const fetchData = async (payload) => {
        setIsLoading(true)
        try {
            const response = await Server.get(`/billed/${payload.by}/?year=${payload.year}&month=${payload.month}&public_id=${payload.public_id}`)
            setBilled(response?.data)
            setIsLoading(false)

            const output = response?.data.reduce((data, row) => {
                data.previous_balance += parseFloat(row.previous_balance);
                data.difference += parseFloat(row.difference);
                data.debit += parseFloat(row.debit);
                return data;
            }, {previous_balance: 0.00, difference: 0, debit: 0.00});
            setPinnedBottomRowData([
                {
                    previous_balance: parseFloat(output.previous_balance).toFixed(2),
                    difference: parseFloat(output.difference).toFixed(0),
                    debit: parseFloat(output.debit).toFixed(2),
                }
            ]);
        } catch (error) {
            setIsLoading(false)
            if(error?.response?.status === 401){
                localStorage.removeItem('user')
                navigate('/login')
            }else if (error?.response?.status === 403){
                toast.error(error?.response?.data?.detail || 'Forbidden');
                navigate('/');
            }else {
                toast.error(error?.response?.data?.detail || 'Failed to load billed details')
            }
        }
    }

    const handleClose = () => {
        if (!isDeleting) setShow(false)
    }
    const handleShow = (public_id) => {
        setShow(true)
        setPublicID(public_id)
    };

    const handleDelete = async (public_id) => {
        setIsDeleting(true)
        try {
            const response = await Server.delete(`/billed/${public_id}`)
            toast.success(response.data.detail)
            setBilled(prev => prev.filter((bill) => bill.public_id !== public_id))
            setShow(false)
        } catch (error) {
            if(error?.response?.status === 401){
                navigate('/login')
            }else if (error?.response?.status === 403){
                toast.error(error?.response?.data?.detail || 'Forbidden');
                navigate('/');
            }else{
                toast.error(error?.response?.data?.detail || 'Failed to cancel bill')
            }
        } finally {
            setIsDeleting(false)
        }
    }

    const onFirstDataRendered = (params) => {
        params.api.autoSizeAllColumns();
    }

    const onGridSizeChanged = (params) => {
        params.api.autoSizeAllColumns();
    }

    let gridApi

    const onGridReady=(params)=>{
        gridApi=params.api
    }
      const onExportClick=()=>{
        gridApi.exportDataAsCsv({ fileName: `${title}.csv` })
    }

    const getRowStyle = (params) => {
        if (params.node.rowPinned) {
          return {
            backgroundColor: '#f8f9fa',
            fontWeight: 'bold',
            color: '#000',
          };
        }
        return {};
    };

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
       <div className="row">
            <div className="col-md-6">
                <BackButton
                    to="/billed"
                    state={{ year: location.state?.data?.year, month: location.state?.data?.month, by: location.state?.data?.by }}
                    label="Back to Summary"
                />
            </div>
            <div className="col-md-6">
                <button onClick={()=>onExportClick()} className="btn btn-success mb-2" style={{float: 'right'}}><FaFileExport/> &nbsp;Export</button>
            </div>
        </div>
        <div className="card mt-0">
            <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                    <h3 className="card-title">{title}</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            onFirstDataRendered={onFirstDataRendered}
                            onGridSizeChanged={onGridSizeChanged}
                            suppressMenuHide={true}

                            rowData={billed}
                            domLayout={"normal"}
                            rowSelection={"multiple"}

                            animateRows={true}

                            pagination={false}
                            onGridReady={onGridReady}

                            resizable={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}


                            ref={gridRef}
                            pinnedBottomRowData={pinnedBottomRowData}
                            getRowStyle={getRowStyle}
                            columnDefs={columnDefs}
                        ></AgGridReact>{" "}
                    </div>
                    </div>
                </div>
            </div>
        </div>

        <Modal show={show} onHide={handleClose} backdrop={isDeleting ? 'static' : true}>
            <Modal.Header closeButton={!isDeleting}>
            <Modal.Title>Delete Alert</Modal.Title>
            </Modal.Header>
            <Modal.Body>Are you sure?</Modal.Body>
            <Modal.Footer>
            <Button variant="primary" onClick={handleClose} disabled={isDeleting}>
                No
            </Button>
            <Button variant="danger" onClick={() => handleDelete(publicID)} disabled={isDeleting}>
                {isDeleting ? 'Deleting\u2026' : 'Yes'}
            </Button>
            </Modal.Footer>
        </Modal>
      <br/>
    </>
  )
}

export default BilledDetail
